import { createContext, Dispatch, ReactNode, SetStateAction, useContext, useMemo, useState } from 'react';

export type ZitadelLoginInfo = {
    isMfaRegistrationRequired: boolean;
    isTotpChallengeRequired: boolean;
    isOtpEmailChallengeRequired: boolean;
}

export type ZitadelSession = {
    id: string;
    token: string;
    metadata: ZitadelSessionMetadata;
};

type ZitadelSessionMetadata = {
    userEmail: string;
}

export type ZitadelTotpRegistration = {
    secret: string;
    uri: string;
};

export type ZitadelContextType = {
    loginInfo: ZitadelLoginInfo | null;
    setLoginInfo: Dispatch<SetStateAction<ZitadelLoginInfo | null>>;
    clearLoginInfo: () => void;
    session: ZitadelSession | null;
    setSession: Dispatch<SetStateAction<ZitadelSession | null>>;
    clearSession: () => void;
    totpRegistration: ZitadelTotpRegistration | null;
    setTotpRegistration: Dispatch<SetStateAction<ZitadelTotpRegistration | null>>;
    clearTotpRegistration: () => void;
};

type ZitadelProviderProps = {
    children: ReactNode;
}

const ZitadelContext = createContext<ZitadelContextType | undefined>(undefined);

export const ZitadelContextProvider = ({ children }: ZitadelProviderProps) => {
    const [loginInfo, setLoginInfo] = useState<ZitadelLoginInfo | null>(null);
    const [session, setSession] = useState<ZitadelSession | null>(null);
    const [totpRegistration, setTotpRegistration] = useState<ZitadelTotpRegistration | null>(null);

    const value = useMemo(() => ({
        loginInfo,
        setLoginInfo,
        clearLoginInfo: () => setLoginInfo(null),

        session,
        setSession,
        clearSession: () => setSession(null),

        totpRegistration,
        setTotpRegistration,
        clearTotpRegistration: () => setTotpRegistration(null),
    }), [
        loginInfo,
        setLoginInfo,
        session,
        setSession,
        totpRegistration,
        setTotpRegistration,
    ]);

    return (
        <ZitadelContext.Provider value={value}>
            {children}
        </ZitadelContext.Provider>
    );
};

export const useZitadel = (): ZitadelContextType => {
    const context = useContext(ZitadelContext);

    if (!context) {
        throw new Error('useZitadel must be used within a ContextProvider');
    }

    return context;
};
